import React, { useState, useEffect, useRef } from "react";
import placeholderImage from "../../assets/placeholder.webp";

const LazyImage = ({ src, alt, className, fallback = <img src={placeholderImage} alt={alt} className="w-full h-full object-cover" /> }) => {
  const [isLoaded, setIsLoaded] = useState(false);
  const [hasError, setHasError] = useState(false);
  const imgRef = useRef(null);

  useEffect(() => {
    if (!src) {
      setHasError(true);
      return;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            const img = new Image();
            img.src = src;
            img.onload = () => {
              setIsLoaded(true);
              if (imgRef.current) {
                imgRef.current.src = src;
              }
            };
            img.onerror = () => {
              setHasError(true);
            };
            observer.unobserve(entry.target);
          }
        });
      },
      {
        rootMargin: "50px",
        threshold: 0.1,
      },
    );

    if (imgRef.current) {
      observer.observe(imgRef.current);
    }

    return () => {
      if (imgRef.current) {
        observer.unobserve(imgRef.current);
      }
    };
  }, [src]);

  if (hasError || !src) {
    return (
      <div
        className={`${className} bg-gray-200 dark:bg-gray-700 flex items-center justify-center`}
      >
        {fallback}
      </div>
    );
  }

  return (
    <>
      <img
        ref={imgRef}
        src={isLoaded ? src : ""}
        alt={alt}
        className={`${className} transition-opacity duration-300 ${
          isLoaded ? "opacity-100" : "opacity-0"
        }`}
        loading="lazy"
      />
      {!isLoaded && (
        <div
          className={`${className} bg-gray-200 dark:bg-gray-700 animate-pulse`}
        />
      )}
    </>
  );
};

export default LazyImage;
